import { MediaRecorder, register } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';

import { DEFAULT_AUDIO_MIME_TYPE, DEFAULT_SAMPLE_RATE } from '../../constants/audio.ts';
import { MediaRecorderType } from '../../types/audio.types.ts';

import { MultiAudioRecorderUtil } from './multi-audio-recorder.util.ts';

const DEFAULT_VOLUME = 1;
const DEFAULT_THRESHOLD = -15;
const DEFAULT_RATIO = 20;
const DEFAULT_ATTACK = 0.001;
const DEFAULT_RELEASE = 0.25;

export class AudioContextUtil {
  private static _instance: AudioContextUtil;
  private playingAudioContext: AudioContext | null = null;
  private playingSource: AudioBufferSourceNode | null = null;
  private stream: MediaStream | null = null;
  private recordingAudioContext: AudioContext | null = null;
  private isWavEncoderInitialized = false;

  public static get instance(): AudioContextUtil {
    if (!AudioContextUtil._instance) {
      AudioContextUtil._instance = new AudioContextUtil();
    }
    return AudioContextUtil._instance;
  }

  private constructor() {
    //
  }

  get context(): AudioContext | null {
    return this.playingAudioContext;
  }

  async getMultiAudioRecorder({ mimeType }: { mimeType: string }): Promise<MultiAudioRecorderUtil> {
    const stream = await this.init();

    if (!this.isWavEncoderInitialized && mimeType === DEFAULT_AUDIO_MIME_TYPE) {
      await register(await connect());
      this.isWavEncoderInitialized = true;
    }

    // We need to resample the audio to 16000 Hz for it to work with SpeechSuper
    // Create source and destination nodes for resampling
    const mediaStreamSource = new MediaStreamAudioSourceNode(this.recordingAudioContext!, {
      mediaStream: stream,
    });
    const mediaStreamDestination = new MediaStreamAudioDestinationNode(this.recordingAudioContext!, {
      channelCount: 1,
    });

    // Connect the nodes to enable resampling
    mediaStreamSource.connect(mediaStreamDestination);

    return new MultiAudioRecorderUtil({
      [MediaRecorderType.GRADING]: new MediaRecorder(mediaStreamDestination.stream, { mimeType }),
      [MediaRecorderType.STORAGE]: new MediaRecorder(stream, { mimeType }),
    });
  }

  async getStream(): Promise<MediaStream> {
    if (!this.stream) {
      this.stream = await this.init();
    }
    return this.stream;
  }

  private async initAudioContext(stream: MediaStream): Promise<void> {
    if (!this.playingAudioContext) {
      this.playingAudioContext = new AudioContext({
        latencyHint: 'interactive',
      });
    }
    if (this.playingAudioContext.state === 'suspended') {
      await this.playingAudioContext.resume();
    }

    if (!this.recordingAudioContext) {
      this.recordingAudioContext = new AudioContext({
        latencyHint: 'interactive',
        sampleRate: DEFAULT_SAMPLE_RATE,
      });
    }
    if (this.recordingAudioContext.state === 'suspended') {
      await this.recordingAudioContext.resume();
    }

    this.stream = stream;
  }

  async init(): Promise<MediaStream> {
    if (this.stream) return this.stream;
    const stream = await window.navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false,
      },
    });
    await this.initAudioContext(stream);
    return stream;
  }

  async playAudio(audioBuffer: AudioBuffer, volume = DEFAULT_VOLUME): Promise<void> {
    await this.init();
    if (!this.playingAudioContext) return Promise.resolve();
    const source = this.playingAudioContext.createBufferSource();
    source.buffer = audioBuffer;
    this.playingSource = source;

    const preparedSource = this.prepareSource(source, volume);
    preparedSource.connect(this.playingAudioContext.destination);

    source.start();
    return new Promise((resolve) => {
      source.onended = (): void => {
        this.playingSource = null;
        resolve();
      };
    });
  }

  async prepareMediaElementSource(mediaElement: HTMLMediaElement, volume: number = DEFAULT_VOLUME): Promise<void> {
    await this.init();
    if (!this.playingAudioContext) return;
    const source = this.playingAudioContext.createMediaElementSource(mediaElement);
    const preparedSource = this.prepareSource(source, volume);
    preparedSource.connect(this.playingAudioContext.destination);
  }

  private prepareSource(source: AudioNode, volume: number = DEFAULT_VOLUME, threshold = DEFAULT_THRESHOLD): AudioNode {
    return this.applyLimiter(this.applyGain(source, volume), threshold);
  }

  private applyGain(source: AudioNode, volume: number): AudioNode {
    if (!this.playingAudioContext) return source;
    const gainNode = this.playingAudioContext.createGain();
    gainNode.gain.value = volume;
    source.connect(gainNode);
    return gainNode;
  }

  private applyLimiter(source: AudioNode, threshold = DEFAULT_THRESHOLD): AudioNode {
    if (!this.playingAudioContext) return source;

    const compressor = this.playingAudioContext.createDynamicsCompressor();
    compressor.threshold.value = threshold;
    compressor.ratio.value = DEFAULT_RATIO;
    compressor.attack.value = DEFAULT_ATTACK;
    compressor.release.value = DEFAULT_RELEASE;

    source.connect(compressor);
    return compressor;
  }

  async decodeAudioDataFromBase64(base64: string): Promise<AudioBuffer | null> {
    await this.init();
    if (!this.playingAudioContext) return null;
    const bytes = this.base64ToBytesArray(base64);
    if (bytes === null) return null;
    return this.playingAudioContext.decodeAudioData(bytes.buffer);
  }

  base64ToBytesArray(base64: string): Uint8Array | null {
    if (base64 === '') return null;
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  stopPlaying(): void {
    if (!this.playingAudioContext) return;
    if (!this.playingSource) return;
    this.playingSource.stop();
    this.playingSource.disconnect();
    this.playingSource = null;
  }
}

export const getAudioContextUtil = (): AudioContextUtil => AudioContextUtil.instance;
